import getWindow from 'Utils/get-window';

import { isAdsIdentityEnabled } from './ads-config';
import { registerSecureSignalProvider, setPublisherProvidedId } from './gpt';
import {
  getGppConsent,
  getTcfConsentData,
  getUspConsentString,
} from './consent';

/**
 * Ads identity, phase 1 (WEB_ADS_IDENTITY): identified traffic monetizes at
 * a large multiple of anonymous traffic (~4.7x on the mobile apps), so when
 * a Flipboard user is logged in — and only when consent permits — attach
 * privacy-safe identity to the web display-ad stack:
 *
 *  1. GPT PPID (Publisher Provided ID): a salted SHA-256 of the Flipboard
 *     user id, set via googletag.pubads().setPublisherProvidedId() before
 *     the first slot is defined. Never raw PII; stable per user so GAM can
 *     use it for frequency capping / audience.
 *  2. LiveRamp RampID envelope: fetched from LiveRamp's envelope API keyed
 *     by sha256(lowercased email) — the same identifier the mobile ATS SDKs
 *     use — cached in localStorage for 15 days (mirroring iOS
 *     LiveRampAdManager's maxCachedEnvelopeAge; 15 days is LiveRamp's locked
 *     envelope expiration). The envelope feeds two demand paths:
 *       - GPT secure signals (id 'liveramp.com') → AdX open auction;
 *       - flint's `eids` request param as `liveramp.com,<envelope>` — the
 *         exact param/format iOS (FLFlintAdRequest.m,
 *         kFLLiveRampIDParameterName = "eids") and Android (FlintClient.kt,
 *         eids = "liveramp.com,$envelopeId") send, which flint parses into
 *         OpenRTB user.ext.eids for bidders
 *         (FullPageAndNativeAdQueryRequest.java).
 *
 * Consent gating (checked before ANY identity is derived or sent):
 *  - GDPR traffic (CMP reports gdprApplies): require a TCF string AND
 *    purpose 1 (storage/access) consent. No answer from the CMP within the
 *    timeout → fail CLOSED (no identity) — the reverse of the ad path
 *    itself, which fails open.
 *  - US traffic: honor the IAB US Privacy string's opt-out-of-sale signal
 *    (third character 'Y' → no identity).
 *
 * EMAIL SEAM (v1 limitation): the web client does not reliably have the
 * logged-in user's email. The server session (server/lib/auth.ts) holds only
 * uid-bearing tokens — no email — and Redux only carries an email after the
 * flap /flipboard/userInfo response has been loaded into profile.userInfo
 * (settings pages and a few post-onboarding flows; the flipboard service
 * entry in myServices carries `email`, see profile-settings.js). So v1 uses
 * the email opportunistically when it is already in state, and the PPID path
 * works for every logged-in user. Full envelope coverage needs either a
 * server-computed sha256(lowercased email) exposed in preloaded state (flap
 * userInfo call during SSR) or a client fetchUserInfo() on ad pages — the
 * seam is selectAdsIdentityEmail() below.
 */

// Constant salt for the PPID derivation. Not a secret (it ships in the
// bundle); its purpose is to make the PPID a Flipboard-ads-specific opaque
// identifier so the GAM id space can't be trivially joined against raw
// Flipboard user ids by third parties. Changing it rotates every PPID.
export const PPID_SALT = 'flipboard-web-ads-identity-v1';

// LiveRamp envelope API — the same endpoint/config the mobile ATS SDK
// integration resolves to: pid 13428, it=4 (identifier type: sha256 email),
// iv=<sha256(lowercased email)>. The browser attaches the page Origin
// automatically; LiveRamp only answers for allowlisted origins, so
// https://flipboard.com must be added to the ATS placement's domain
// allowlist before this returns envelopes in production.
export const LIVERAMP_ENVELOPE_URL =
  'https://api.rlcdn.com/api/identity/v2/envelope';
export const LIVERAMP_PID = '13428';
export const ENVELOPE_FETCH_TIMEOUT_MS = 1000;

// Mirror iOS: a cached envelope is usable for 15 days (LiveRamp's locked
// "Envelope Expiration Time"), keyed to the identifier hash so another
// account's envelope is never reused.
export const ENVELOPE_TTL_MS = 15 * 24 * 60 * 60 * 1000;
export const ENVELOPE_STORAGE_KEY = 'fl.ads.identity.envelope';

// How long flint's eids provider waits for the envelope before letting the
// flint query proceed without it (the shared promise stays pending; later
// slots pick the envelope up). Cache hits resolve immediately.
export const FLINT_EIDS_WAIT_MS = 300;

type IdentityWindow = {
  fetch?: typeof fetch;
  localStorage?: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
  };
};

const getIdentityWindow = (): IdentityWindow =>
  getWindow() as unknown as IdentityWindow;

/* ------------------------------------------------------------------ */
/* SHA-256 (synchronous, dependency-free)                              */
/* ------------------------------------------------------------------ */

// Compact synchronous SHA-256 so the PPID is available before the first
// GPT slot without awaiting SubtleCrypto (async, and unavailable in some
// jsdom/non-secure contexts). FIPS 180-4; verified against the standard
// "abc" test vector in identity.test.js.

/* eslint-disable no-bitwise */
const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

const utf8Bytes = (input: string): number[] => {
  const bytes: number[] = [];
  for (let i = 0; i < input.length; i++) {
    let code = input.charCodeAt(i);
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code >= 0xd800 && code <= 0xdbff && i + 1 < input.length) {
      // surrogate pair
      code =
        0x10000 + ((code - 0xd800) << 10) + (input.charCodeAt(++i) - 0xdc00);
      bytes.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    } else {
      bytes.push(
        0xe0 | (code >> 12),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return bytes;
};

export const sha256Hex = (input: string): string => {
  const bytes = utf8Bytes(input);
  const bitLength = bytes.length * 8;

  // Padding: 0x80, zeros, 64-bit big-endian length.
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) {
    bytes.push(0);
  }
  // High 32 bits of the length (message lengths here never exceed 2^32 bits,
  // but write both words for correctness).
  bytes.push(
    Math.floor(bitLength / 0x100000000) >>> 24,
    (Math.floor(bitLength / 0x100000000) >>> 16) & 0xff,
    (Math.floor(bitLength / 0x100000000) >>> 8) & 0xff,
    Math.floor(bitLength / 0x100000000) & 0xff,
    (bitLength >>> 24) & 0xff,
    (bitLength >>> 16) & 0xff,
    (bitLength >>> 8) & 0xff,
    bitLength & 0xff,
  );

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const w = new Array<number>(64);
  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let t = 0; t < 16; t++) {
      const i = offset + t * 4;
      w[t] =
        (bytes[i] << 24) |
        (bytes[i + 1] << 16) |
        (bytes[i + 2] << 8) |
        bytes[i + 3];
    }
    for (let t = 16; t < 64; t++) {
      const s0 = rotr(w[t - 15], 7) ^ rotr(w[t - 15], 18) ^ (w[t - 15] >>> 3);
      const s1 = rotr(w[t - 2], 17) ^ rotr(w[t - 2], 19) ^ (w[t - 2] >>> 10);
      w[t] = (w[t - 16] + s0 + w[t - 7] + s1) | 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;

    for (let t = 0; t < 64; t++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + SHA256_K[t] + w[t]) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) | 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) | 0;
    }

    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map((word) => (word >>> 0).toString(16).padStart(8, '0'))
    .join('');
};
/* eslint-enable no-bitwise */

/* ------------------------------------------------------------------ */
/* PPID                                                                */
/* ------------------------------------------------------------------ */

/**
 * Derive the GPT Publisher Provided ID for a logged-in Flipboard user:
 * salted SHA-256 of the user id, hex-encoded (64 alphanumeric characters —
 * within GPT's 22-150 char alphanumeric requirement, never raw PII, stable
 * per user). Null when logged out (uid 0 / missing).
 */
export const getPpid = (uid: number | undefined | null): string | null => {
  if (!uid || uid <= 0) {
    return null;
  }
  return sha256Hex(`${PPID_SALT}:${uid}`);
};

/**
 * The LiveRamp identifier: sha256 of the trimmed, lowercased email (the
 * canonicalization both mobile ATS SDK integrations use). Null when no
 * email is available (see the EMAIL SEAM note above).
 */
export const getEmailIdentifierHash = (
  email: string | undefined | null,
): string | null => {
  const normalized = email?.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  return sha256Hex(normalized);
};

/* ------------------------------------------------------------------ */
/* Consent                                                             */
/* ------------------------------------------------------------------ */

/**
 * Position of the opt-out-of-sale signal in the IAB US Privacy string
 * (e.g. "1YNN": version, notice, OPT-OUT, LSPA).
 */
const USP_OPT_OUT_INDEX = 2;

/**
 * Lowest IAB GPP section id belonging to a US privacy regime. Sections below
 * it are non-US (2 tcfeuv2, 5 tcfcav1); 6 is uspv1 and everything above is a
 * US national or state section, a list IAB keeps extending (7 usnatv1,
 * 8 uscav1 ... 13 usflv1, 15 usorv1, 16 ustxv1 and beyond).
 *
 * A floor rather than an enumerated set, so a section added after this ships
 * is treated as US scope instead of silently falling through to "granted".
 * Mis-classifying a future non-US section only costs identity on that
 * traffic, which is the safe direction.
 */
const US_PRIVACY_FIRST_GPP_SECTION = 6;

/**
 * Whether consent permits sending identity with ad requests. Fail CLOSED:
 * identity is an enhancement, so any ambiguity means "no identity" (the ad
 * request itself still proceeds).
 *
 *  - CMP unanswered (stub only / timeout) → false: we cannot establish the
 *    regime, so no identity.
 *  - gdprApplies → require a TCF string and purpose 1 consent.
 *  - US privacy applies → require a readable opt-out signal saying the user
 *    has NOT opted out. Unreadable signal → false.
 *  - rest of world → no applicable restriction, identity proceeds.
 */
export const isIdentityConsentGranted = async (): Promise<boolean> => {
  const tcData = await getTcfConsentData();
  if (!tcData) {
    return false;
  }
  // Fail closed on an unknown regime: gdprApplies must be explicitly true
  // or false. An absent/undefined value means the CMP hasn't resolved the
  // regime — identity must not proceed on a guess.
  if (tcData.gdprApplies !== true && tcData.gdprApplies !== false) {
    return false;
  }
  if (tcData.gdprApplies) {
    const purposeOneConsent = !!tcData.purpose?.consents?.['1'];
    return !!tcData.tcString && purposeOneConsent;
  }
  return isUsPrivacyIdentityGranted();
};

/**
 * The non-GDPR branch, split out because US-privacy scope and rest-of-world
 * must not be treated alike.
 *
 * Scope comes from the GPP applicableSections, not from the presence of a
 * legacy USP string: a user in a US state regime whose USP string we cannot
 * read is exactly the case that must fail closed, and keying off the string
 * itself would miss it. Rest-of-world traffic (no US section applicable)
 * never carried a US restriction and is unaffected.
 *
 * A readable legacy `__uspapi` string stays authoritative wherever it is
 * present, so existing opt-out handling is unchanged. Once the CMP serves
 * GPP only that read returns null and US-scope traffic fails closed, until
 * the shared GPP→us_privacy derivation
 * lands (services/specs/ads/us-privacy-signal-migration.md §5). Losing
 * identity on US traffic is the acceptable side of that trade; attaching a
 * PPID or a RampID for someone who opted out of sale is not.
 */
const isUsPrivacyIdentityGranted = async (): Promise<boolean> => {
  // Read both concurrently: each carries its own CONSENT_TIMEOUT_MS budget,
  // and awaiting them in sequence would make a page with two non-answering
  // stubs wait twice that before any ad slot could initialise.
  const [uspString, gpp] = await Promise.all([
    getUspConsentString(),
    getGppConsent(),
  ]);
  if (uspString && uspString.length > USP_OPT_OUT_INDEX) {
    return uspString.charAt(USP_OPT_OUT_INDEX).toUpperCase() !== 'Y';
  }
  // No readable legacy string, so scope decides. A null GPP answer means the
  // CMP never reached signalStatus "ready", i.e. scope is unestablished, and
  // the fail-closed contract above applies to it as much as to a known US
  // regime. Only an explicit non-US answer grants identity here.
  if (!gpp?.applicableSections) {
    return false;
  }
  return !gpp.applicableSections.some(
    (section) => section >= US_PRIVACY_FIRST_GPP_SECTION,
  );
};

/* ------------------------------------------------------------------ */
/* LiveRamp envelope fetch + cache                                     */
/* ------------------------------------------------------------------ */

type CachedEnvelope = {
  idHash: string;
  envelope: string;
  fetchedAt: number;
};

/**
 * Read the cached envelope from localStorage. Only valid when it belongs to
 * the same identifier (another account's envelope must never be sent —
 * mirrors iOS) and is younger than the 15-day TTL.
 */
export const readCachedEnvelope = (
  idHash: string,
  now: number = Date.now(),
): string | null => {
  const storage = getIdentityWindow().localStorage;
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(ENVELOPE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const cached = JSON.parse(raw) as CachedEnvelope;
    if (
      !cached ||
      cached.idHash !== idHash ||
      typeof cached.envelope !== 'string' ||
      !cached.envelope ||
      typeof cached.fetchedAt !== 'number' ||
      now - cached.fetchedAt >= ENVELOPE_TTL_MS ||
      now < cached.fetchedAt
    ) {
      return null;
    }
    return cached.envelope;
  } catch (_) {
    return null;
  }
};

export const writeCachedEnvelope = (
  idHash: string,
  envelope: string,
  now: number = Date.now(),
): void => {
  const storage = getIdentityWindow().localStorage;
  if (!storage) {
    return;
  }
  try {
    storage.setItem(
      ENVELOPE_STORAGE_KEY,
      JSON.stringify({ idHash, envelope, fetchedAt: now } as CachedEnvelope),
    );
  } catch (_) {
    // Quota/security errors: the envelope just won't be cached.
  }
};

/**
 * Fetch a RampID envelope from LiveRamp's envelope API. Fail-open (null) on
 * timeout, network error, non-2xx (including the 403 an un-allowlisted
 * Origin gets) or an unexpected body. Accepts both response shapes LiveRamp
 * uses: {"envelopes":[{"value":...}]} and {"envelope":...}.
 */
export const fetchLiveRampEnvelope = async (
  idHash: string,
): Promise<string | null> => {
  const win = getIdentityWindow();
  // Capture the function: TS narrowing does not survive into the nested
  // async closure below.
  const fetchFn = win.fetch;
  if (typeof fetchFn !== 'function') {
    return null;
  }
  const controller =
    typeof AbortController === 'function' ? new AbortController() : null;
  // The hard budget is a Promise.race — AbortController (when available)
  // additionally cancels the network request, but the race guarantees this
  // resolves within the budget even where AbortController is missing (an
  // un-aborted fetch would otherwise hold the envelope promise open
  // indefinitely).
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort();
      resolve(null);
    }, ENVELOPE_FETCH_TIMEOUT_MS);
  });
  const request = (async (): Promise<string | null> => {
    const params = new URLSearchParams();
    params.set('pid', LIVERAMP_PID);
    // it=4: identifier type sha256(email); iv: the identifier value.
    params.set('it', '4');
    params.set('iv', idHash);
    const response = await fetchFn(
      `${LIVERAMP_ENVELOPE_URL}?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'omit',
        signal: controller?.signal,
      },
    );
    if (!response.ok) {
      return null;
    }
    const body = (await response.json()) as {
      envelope?: unknown;
      envelopes?: Array<{ value?: unknown }>;
    } | null;
    const envelope =
      (typeof body?.envelope === 'string' && body.envelope) ||
      (Array.isArray(body?.envelopes) &&
        typeof body?.envelopes[0]?.value === 'string' &&
        body?.envelopes[0]?.value) ||
      null;
    return envelope || null;
  })().catch(() => null);
  try {
    return await Promise.race([request, timeout]);
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
};

/**
 * Cache-or-fetch the envelope for an identifier hash: localStorage first
 * (15-day TTL, identifier-keyed), then the envelope API (writing the cache
 * on success). Never rejects.
 */
export const getLiveRampEnvelope = async (
  idHash: string,
): Promise<string | null> => {
  const cached = readCachedEnvelope(idHash);
  if (cached) {
    return cached;
  }
  const envelope = await fetchLiveRampEnvelope(idHash);
  if (envelope) {
    writeCachedEnvelope(idHash, envelope);
  }
  return envelope;
};

/* ------------------------------------------------------------------ */
/* Page-level wiring                                                   */
/* ------------------------------------------------------------------ */

/**
 * Redux selectors for the identity inputs. Deliberately self-contained
 * (mirroring flap-util's getUserFromUserInfo without importing it) so the
 * ad-slot import graph stays light: the logged-in uid always exists in
 * auth state; the email only exists after a flap /flipboard/userInfo
 * response has been loaded into profile.userInfo — the flipboard entry of
 * myServices carries `email` (see profile-settings.js) — which happens on
 * settings pages and some post-onboarding flows, NOT on a typical page load
 * (the EMAIL SEAM above).
 */
export const selectAdsIdentityUid = (state: Flipboard.State): number =>
  (state.auth?.uid as unknown as number) || 0;

export const selectAdsIdentityEmail = (
  state: Flipboard.State,
): string | null => {
  const service = state.profile?.userInfo?.myServices?.find(
    (candidate) => candidate?.service === 'flipboard',
  );
  return service?.email || null;
};

export type AdsIdentityInput = {
  featureFlags?: Partial<Record<string, boolean>>;
  // Logged-in Flipboard user id (auth.uid); 0/undefined when logged out.
  uid?: number;
  // Logged-in user's email when the client happens to have it (see the
  // EMAIL SEAM note in the module docs) — profile.userInfo's flipboard
  // service entry. Undefined/null in the common case.
  email?: string | null;
};

// Page-lifetime identity state: init runs once (all ad slots share it) and
// the envelope promise is what flint's eids provider and the GPT secure
// signal collector await. Client-only (init is called from componentDidMount
// paths), so server renders never share state across requests.
let initPromise: Promise<void> | null = null;
let envelopePromise: Promise<string | null> | null = null;

/** Test hook: reset the page-lifetime identity state. */
export const resetAdsIdentityForTesting = (): void => {
  initPromise = null;
  envelopePromise = null;
};

const initAdsIdentity = async (input: AdsIdentityInput): Promise<void> => {
  const consented = await isIdentityConsentGranted();
  if (!consented) {
    return;
  }
  // PPID: every logged-in user, GDPR/US consent permitting. Enqueued onto
  // googletag.cmd here — ad slots await maybeInitAdsIdentity() before
  // enqueueing defineSlot, so the PPID always precedes the first ad request.
  const ppid = getPpid(input.uid);
  if (ppid) {
    setPublisherProvidedId(ppid);
  }
  // LiveRamp envelope: only when an email is available (see EMAIL SEAM).
  const idHash = getEmailIdentifierHash(input.email);
  if (!idHash) {
    return;
  }
  const sharedEnvelopePromise = getLiveRampEnvelope(idHash);
  envelopePromise = sharedEnvelopePromise;
  // Secure signals: GPT invokes the collector asynchronously and attaches
  // the resolved envelope to AdX requests without ever delaying them. A
  // null envelope rejects the collector, which GPT treats as "no signal".
  registerSecureSignalProvider({
    id: 'liveramp.com',
    collectorFunction: async () => {
      const envelope = await sharedEnvelopePromise;
      if (!envelope) {
        throw new Error('no LiveRamp envelope available');
      }
      return envelope;
    },
  });
};

/**
 * Flag- and consent-gated identity bootstrap, shared by all ad slots: a
 * no-op unless WEB_ADS + WEB_ADS_IDENTITY are active; runs once per page
 * otherwise. Never rejects; resolution is bounded by the CMP consent
 * timeouts, so awaiting it cannot stall an ad request beyond that.
 */
export const maybeInitAdsIdentity = (
  input: AdsIdentityInput,
): Promise<void> => {
  if (!isAdsIdentityEnabled(input.featureFlags)) {
    return Promise.resolve();
  }
  if (!initPromise) {
    initPromise = initAdsIdentity(input).catch(() => {
      // fail open — identity is best-effort, ads proceed without it
    });
  }
  return initPromise;
};

/**
 * flint eids provider (FlintQueryOptions.eidsProvider): resolves
 * `liveramp.com,<envelope>` — the source,id pair format the mobile clients
 * send and flint parses into OpenRTB user.ext.eids — or null when identity
 * is off/unavailable. Waits at most FLINT_EIDS_WAIT_MS so a first-page
 * envelope fetch can't delay the flint query; the slot simply goes without
 * eids and later slots pick the (by then cached) envelope up.
 */
export const getFlintEids = async (): Promise<string | null> => {
  // Await the identity bootstrap first (it is what populates
  // envelopePromise, after the consent check) — ad slots kick it off before
  // querying flint, so a cached envelope resolves within the wait window
  // even on the very first slot.
  const pendingInit = initPromise;
  if (!pendingInit) {
    return null;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const envelope = await Promise.race([
    (async () => {
      await pendingInit;
      return envelopePromise ? await envelopePromise : null;
    })(),
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), FLINT_EIDS_WAIT_MS);
    }),
  ]).finally(() => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  });
  return envelope ? `liveramp.com,${envelope}` : null;
};
